import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { formatarDataHora, formatarDataHoraSegundos, formatarReais, type OfertaRegistrarEntrada } from "@caixaflow/shared";
import { api, ErroApi } from "../api/cliente";
import { useSessao } from "../api/sessao";
import type { Oferta } from "../api/tipos";
import { novoId } from "../lib/id";
import { CamposAutorizacao, useAutorizacaoAdmin } from "./AutorizacaoAdmin";
import { Campo } from "./Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "./Estados";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

export type ItemOferta = { produtoId: string; nome: string; quantidade: number; precoCentavos: number };

/** Ofertas mexem no estoque e nos relatórios (não no caixa). */
function recarregarDepoisDaOferta(qc: ReturnType<typeof useQueryClient>, eventoId: string) {
  qc.invalidateQueries({ queryKey: ["cardapio", eventoId] });
  qc.invalidateQueries({ queryKey: ["estoque", eventoId] });
  qc.invalidateQueries({ queryKey: ["movimentacoes", eventoId] });
  qc.invalidateQueries({ queryKey: ["caixa"] });
  qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("relatorio") || q.queryKey[0] === "ofertas" });
}

/**
 * Registro de oferta: itens entregues sem pagamento (ex.: para convidados, a pedido do pastor).
 * No PDV os itens vêm da comanda e o caixa vai junto; pelo estoque, o ADMIN escolhe os itens.
 * Precisa de internet (a autorização é conferida na hora).
 */
export function FormOferta(props: {
  eventoId: string;
  caixaId?: string;
  /** Itens já escolhidos (comanda do PDV). Sem eles, o formulário mostra a escolha de produtos. */
  itens?: ItemOferta[];
  /** Produtos disponíveis para escolher (lançamento pelo estoque). */
  produtos?: { produtoId: string; nome: string; precoCentavos: number; saldo: number }[];
  onPronto: (oferta: Oferta) => void;
  onVoltar: () => void;
}) {
  const { eventoId, caixaId, produtos = [], onPronto, onVoltar } = props;
  const [linhas, setLinhas] = useState<{ produtoId: string; quantidade: number }[]>(() =>
    props.itens ? props.itens.map((i) => ({ produtoId: i.produtoId, quantidade: i.quantidade })) : [{ produtoId: "", quantidade: 1 }],
  );
  const [solicitadoPor, setSolicitadoPor] = useState("");
  const [observacao, setObservacao] = useState("");
  // Mesmo id em cada nova tentativa: reenviar não duplica a oferta.
  const [id] = useState(novoId);
  const autorizacao = useAutorizacaoAdmin();
  const qc = useQueryClient();
  const toast = useToast();

  const nomeDe = (produtoId: string) =>
    props.itens?.find((i) => i.produtoId === produtoId)?.nome ?? produtos.find((p) => p.produtoId === produtoId)?.nome ?? "";
  const precoDe = (produtoId: string) =>
    props.itens?.find((i) => i.produtoId === produtoId)?.precoCentavos ?? produtos.find((p) => p.produtoId === produtoId)?.precoCentavos ?? 0;
  const validas = linhas.filter((l) => l.produtoId && l.quantidade > 0);
  const valor = validas.reduce((s, l) => s + l.quantidade * precoDe(l.produtoId), 0);

  const registrar = useMutation({
    mutationFn: () => {
      const corpo: OfertaRegistrarEntrada = {
        id,
        eventoId,
        caixaId,
        itens: validas,
        solicitadoPor,
        observacao: observacao.trim() || undefined,
        autorizacao: autorizacao.valor,
      };
      return api<Oferta>("/ofertas", "POST", corpo);
    },
    onSuccess: (o) => {
      toast(`Oferta #${o.numero} registrada`);
      recarregarDepoisDaOferta(qc, eventoId);
      onPronto(o);
    },
    onError: () => autorizacao.limparSegredo(),
  });
  const erro = registrar.error instanceof ErroApi ? registrar.error : null;

  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (validas.length) registrar.mutate();
      }}
    >
      <p className="mudo pequeno" style={{ margin: 0 }}>
        Itens entregues <b>sem pagamento</b>. Saem do estoque, mas não entram no caixa nem no total arrecadado; aparecem à parte nos
        relatórios, com o valor de referência.
      </p>
      {props.itens ? (
        <div className="card">
          {linhas.map((l) => (
            <div key={l.produtoId} className="linha item-ajuste">
              <span>
                {l.quantidade}× {nomeDe(l.produtoId)}
              </span>
              <span className="mudo">{formatarReais(l.quantidade * precoDe(l.produtoId))}</span>
            </div>
          ))}
          <div className="linha item-ajuste">
            <b>Valor de referência</b>
            <b>{formatarReais(valor)}</b>
          </div>
        </div>
      ) : (
        <div className="form" style={{ gap: 8 }}>
          {linhas.map((l, i) => (
            <div key={i} className="linha-esq" style={{ flexWrap: "nowrap", alignItems: "flex-end" }}>
              <Campo rotulo={i === 0 ? "Produto" : ""}>
                <select
                  required
                  value={l.produtoId}
                  onChange={(e) => setLinhas((ls) => ls.map((x, j) => (j === i ? { ...x, produtoId: e.target.value } : x)))}
                >
                  <option value="">Escolha…</option>
                  {produtos
                    .filter((p) => p.produtoId === l.produtoId || !linhas.some((x) => x.produtoId === p.produtoId))
                    .map((p) => (
                      <option key={p.produtoId} value={p.produtoId}>
                        {p.nome} (saldo {p.saldo})
                      </option>
                    ))}
                </select>
              </Campo>
              <Campo rotulo={i === 0 ? "Qtd" : ""}>
                <input
                  type="number"
                  min={1}
                  max={999}
                  required
                  style={{ width: 80 }}
                  value={l.quantidade}
                  onChange={(e) => setLinhas((ls) => ls.map((x, j) => (j === i ? { ...x, quantidade: Number(e.target.value) } : x)))}
                />
              </Campo>
              {linhas.length > 1 && (
                <button
                  type="button"
                  className="link-botao perigo"
                  aria-label="Tirar este item"
                  onClick={() => setLinhas((ls) => ls.filter((_, j) => j !== i))}
                >
                  Tirar
                </button>
              )}
            </div>
          ))}
          <div className="linha">
            <button
              type="button"
              className="link-botao"
              style={{ padding: 0 }}
              onClick={() => setLinhas((ls) => [...ls, { produtoId: "", quantidade: 1 }])}
            >
              + Outro produto
            </button>
            <span className="mudo pequeno">Valor de referência: {formatarReais(valor)}</span>
          </div>
        </div>
      )}
      {registrar.error && !erro?.campos.length && (
        <div className="aviso erro" role="alert">
          {mensagemDeErro(registrar.error)}
        </div>
      )}
      <Campo rotulo="Pedido de" dica="quem solicitou os itens" erro={erro?.doCampo("solicitadoPor")}>
        <input required minLength={2} value={solicitadoPor} onChange={(e) => setSolicitadoPor(e.target.value)} placeholder="Ex.: Pastor Fulano" />
      </Campo>
      <Campo rotulo="Observação" dica="opcional">
        <input value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: para os convidados da mesa principal" />
      </Campo>
      <CamposAutorizacao autorizacao={autorizacao} erro={(c) => erro?.doCampo(c)} />
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onVoltar}>
          Voltar
        </button>
        <button type="submit" className="btn primario" disabled={registrar.isPending || !validas.length}>
          {registrar.isPending ? "Registrando…" : "Registrar oferta"}
        </button>
      </div>
    </form>
  );
}

/** Detalhe de uma oferta; o ADMIN pode cancelar (o estoque volta). */
export function ModalOferta({ ofertaId, onFechar }: { ofertaId: string | null; onFechar: () => void }) {
  return (
    <Modal aberto={ofertaId !== null} titulo="Oferta" onFechar={onFechar}>
      {ofertaId && <ConteudoOferta key={ofertaId} ofertaId={ofertaId} onFechar={onFechar} />}
    </Modal>
  );
}

function ConteudoOferta({ ofertaId, onFechar }: { ofertaId: string; onFechar: () => void }) {
  const { data: sessao } = useSessao();
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const oferta = useQuery({ queryKey: ["oferta", ofertaId], queryFn: () => api<Oferta>(`/ofertas/${ofertaId}`) });
  const cancelar = useMutation({
    mutationFn: () => api<Oferta>(`/ofertas/${ofertaId}/cancelar`, "POST", { motivo }),
    onSuccess: (o) => {
      toast(`Oferta #${o.numero} cancelada`, "info");
      qc.setQueryData(["oferta", ofertaId], o);
      recarregarDepoisDaOferta(qc, o.evento.id);
      onFechar();
    },
  });

  if (oferta.isPending) return <Carregando />;
  if (oferta.error) return <FalhaAoCarregar erro={oferta.error} tentarDeNovo={() => oferta.refetch()} />;
  const o = oferta.data;
  const podeCancelar = sessao?.usuario.perfil === "ADMIN" && o.status === "VALIDA" && o.evento.status !== "ENCERRADO";

  return (
    <div className="form">
      <div className="card pad" style={{ background: "var(--bg)" }}>
        <div className="linha">
          <b>
            Oferta #{o.numero} · {formatarDataHoraSegundos(o.criadaEm)}
          </b>
          {o.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
        </div>
        <div className="mudo pequeno">
          Pedido de <b>{o.solicitadoPor}</b> · registrada por {o.registradaPor.nome} · autorizada por {o.autorizadaPor.nome}
          {o.caixa ? ` · Caixa #${o.caixa.numero}` : " · lançada pelo estoque"}
        </div>
        {o.observacao && <div className="pequeno">{o.observacao}</div>}
      </div>
      <div className="card tbl-wrap">
        <table>
          <tbody>
            {o.itens.map((i) => (
              <tr key={i.produtoId}>
                <td className="quebra">
                  {i.quantidade}× {i.nomeProduto}
                </td>
                <td className="r mudo">{formatarReais(i.subtotalCentavos)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Valor de referência</td>
              <td className="r">{formatarReais(o.valorReferenciaCentavos)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {o.cancelamento && (
        <div className="aviso erro">
          Cancelada em {formatarDataHora(o.cancelamento.canceladaEm)} por {o.cancelamento.autorizadoPor.nome}: {o.cancelamento.motivo}
        </div>
      )}
      {cancelando ? (
        <form
          className="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            cancelar.mutate();
          }}
        >
          {cancelar.error && <div className="aviso erro">{mensagemDeErro(cancelar.error)}</div>}
          <Campo rotulo="Motivo do cancelamento">
            <input required minLength={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: lançada em dobro" />
          </Campo>
          <div className="modal-acoes">
            <button type="button" className="btn fantasma" onClick={() => setCancelando(false)}>
              Voltar
            </button>
            <button type="submit" className="btn perigo" disabled={cancelar.isPending}>
              {cancelar.isPending ? "Cancelando…" : "Cancelar oferta"}
            </button>
          </div>
        </form>
      ) : (
        <div className="modal-acoes">
          <button type="button" className="btn fantasma" onClick={onFechar}>
            Fechar
          </button>
          {podeCancelar && (
            <button type="button" className="btn perigo" onClick={() => setCancelando(true)}>
              Cancelar oferta
            </button>
          )}
        </div>
      )}
    </div>
  );
}
